import { useCallback, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { EventFile } from "../types/event";
import { callPymbleApi } from "./usePymbleApi";

/**
 * Attaching documents to an event, through the SAME storage service the rest of
 * My Pymble uses.
 *
 * `storage/file/` is shared by every domain in the platform: the portal's
 * pastoral notes, group pages and feedback all post here (see
 * `my-pymble-portal/src/hooks/useFileUpload.ts`, which this follows). Nothing
 * in the event service needed changing to accept a file, and nothing should be
 * built here that the storage service already does.
 *
 * WHAT IT DOES NOT DO IS RECORD THE FILE ON THE EVENT. `handle_post` writes the
 * blob at `{collection}/{id}/{name}` and hands back its metadata; putting that
 * metadata somewhere is the caller's job, exactly as a note puts it in its own
 * `Files` array. For an event that means `Files` on the event document — which
 * `EventUpdateRequest` does not declare yet, so a save drops it. See
 * `docs/event-documents.md`.
 */

/** Where an event's documents live. `secure/` mirrors the notes container. */
const CONTAINER = "secure/events";

/** The Mongo collection the blob path is grouped under. */
const COLLECTION = "Event";

/**
 * SECURE, so nothing is readable without a signed URL.
 *
 * An event's attachments are risk assessments, costings and permission forms —
 * internal operational documents, some naming students. A public blob URL is
 * forever and unauthenticated, which is the wrong default for every one of
 * them. `resolveUrl` in EventDocuments mints a SAS at the moment one is opened.
 */
const SECURE = true;

/** Searchable in the storage service, the way the notes uploader tags its own. */
const TAGS = ["Event", "Document"];

interface UploadOutcome {
	file?: EventFile;
	error?: string;
}

export function useEventFiles(eventId: string) {
	const { instance } = useMsal();
	const [uploading, setUploading] = useState(false);

	/**
	 * One file, uploaded and described.
	 *
	 * Returns rather than throws, and the caller reports: a drop of four files
	 * where the third is a 12MB scan should attach the other three and say what
	 * happened to that one, not abandon the batch at the failure.
	 */
	const uploadOne = useCallback(
		async (file: File): Promise<UploadOutcome> => {
			const params = new URLSearchParams({
				container: CONTAINER,
				collection: COLLECTION,
				id: eventId,
				secure: String(SECURE),
				tags: TAGS.join(","),
			});

			const body = new FormData();
			body.append("file", file);

			try {
				const res = await callPymbleApi(
					instance,
					"POST",
					`storage/file/?${params.toString()}`,
					{
						data: body,
						/* Left to the browser to complete. Axios sets the boundary
						   itself when given FormData; naming the type without one
						   produces a body the server cannot parse. */
						headers: { "Content-Type": "multipart/form-data" },
					},
				);
				if (res?.Result === "Uploaded" && res.File) {
					return { file: res.File as EventFile };
				}
				return { error: `"${file.name}" could not be attached.` };
			} catch {
				/* No detail surfaced: a storage error can echo the request, and the
				   path carries the event id. */
				return { error: `"${file.name}" could not be attached.` };
			}
		},
		[eventId, instance],
	);

	/**
	 * A batch, one at a time.
	 *
	 * Sequential rather than `Promise.all`: these are 10MB documents on school
	 * wifi, and four at once is how the slowest of them times out. The gallery
	 * shows one spinner either way.
	 */
	const upload = useCallback(
		async (files: File[]): Promise<{ files: EventFile[]; errors: string[] }> => {
			setUploading(true);
			const attached: EventFile[] = [];
			const errors: string[] = [];
			try {
				for (const file of files) {
					const outcome = await uploadOne(file);
					if (outcome.file) attached.push(outcome.file);
					if (outcome.error) errors.push(outcome.error);
				}
			} finally {
				setUploading(false);
			}
			return { files: attached, errors };
		},
		[uploadOne],
	);

	/**
	 * A SAS query string for the container, valid an hour.
	 *
	 * `secure`, which is the CONTAINER — `secure/events` is the container plus a
	 * prefix, and Azure signs the container. The portal derives the same value
	 * with `file.Container.split("/")[0]` (see `fileViewerResolve.withSas`);
	 * this app only ever writes to the one container, so it is named.
	 *
	 * Container-wide and minted per visit rather than per file. Worth knowing
	 * that "container-wide" is doing a lot of work there: the signature opens
	 * everything in `secure`, not just this event's documents, which is the case
	 * `docs/event-documents.md` argues should move to per-blob signing behind
	 * the event's own authorisation.
	 */
	const sasQuery = useCallback(async (): Promise<string> => {
		try {
			const res = await callPymbleApi(instance, "GET", "storage/sas/", {
				params: { container: "secure", expiry: 60 },
			});
			return res?.SasQueryString ?? "";
		} catch {
			return "";
		}
	}, [instance]);

	return { upload, uploading, sasQuery };
}
